import React, { useState } from "react";
import { useLang } from "@/context/LanguageContext";

/**
 * Smart image: tries .jpg first, falls back to .svg placeholder automatically.
 * Drop /public/images/<name>.jpg and it will replace the placeholder.
 */
function SmartProductImage({ name, alt, className = "" }) {
  const [src, setSrc] = useState(`/images/${name}.jpg`);
  const [isFallback, setIsFallback] = useState(false);
  return (
    <div className={`relative w-full h-full ${className}`}>
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        loading="lazy"
        onError={() => {
          if (!isFallback) {
            setSrc(`/images/${name}.svg`);
            setIsFallback(true);
          }
        }}
      />
    </div>
  );
}

export default function ProductShowcase() {
  const { t } = useLang();
  const s = t.showcase;

  const tiles = [
    { name: "stand-1", title: s.tiles[0].title, body: s.tiles[0].body, tall: true },
    { name: "stand-2", title: s.tiles[1].title, body: s.tiles[1].body, tall: false },
    { name: "stand-3", title: s.tiles[2].title, body: s.tiles[2].body, tall: true },
  ];

  return (
    <section
      id="product-showcase"
      className="section-y bg-brand-paper"
      data-testid="showcase-section"
    >
      <div className="container-x">
        <div className="max-w-3xl">
          <span className="overline">{s.overline}</span>
          <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-ink text-balance">
            {s.title}
          </h2>
          <p className="mt-6 text-base md:text-lg text-brand-graphite leading-relaxed text-pretty">
            {s.body}
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-4 md:gap-5">
          {tiles.map((t2, i) => (
            <figure
              key={t2.name}
              className={`relative rounded-3xl overflow-hidden border border-brand-mist bg-white group transition-all duration-500 hover:border-brand-champagne/60 hover:shadow-[0_20px_60px_-25px_rgba(10,10,10,0.25)] ${
                t2.tall ? "md:row-span-2" : ""
              }`}
              data-testid={`showcase-tile-${i}`}
            >
              <div className={`relative w-full ${t2.tall ? "aspect-[4/5]" : "aspect-[4/3]"}`}>
                <SmartProductImage name={t2.name} alt={t2.title} />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              <figcaption className="absolute left-0 right-0 bottom-0 p-5 md:p-6 text-white translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                <div className="font-heading text-lg md:text-xl font-medium">{t2.title}</div>
                <div className="mt-1 text-sm text-white/80 leading-relaxed">{t2.body}</div>
              </figcaption>
              <figcaption className="p-5 md:p-6 border-t border-brand-mist group-hover:opacity-0 transition-opacity duration-300">
                <div className="font-heading text-base md:text-lg font-medium text-brand-ink">
                  {t2.title}
                </div>
                <div className="mt-1 text-sm text-brand-graphite leading-relaxed">{t2.body}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
